// Download size routing and the user-facing copy for the GFE response wall.
//
// Pure and dependency-free on purpose, same split as `upload-limits.ts`
// (ENG-3458 / ENG-1820). The server constant lives in
// `railyard.download_direct.DIRECT_DOWNLOAD_THRESHOLD_BYTES`; a Python test
// asserts the two agree.

// Cloud Run / the Google Front End hard-cap HTTP/1 *response* bodies at 32 MB.
// An authed `fetch()` of a ~29 MB zip into a blob URL dies as an opaque
// `TypeError: Failed to fetch` — the same CORS-less refusal that used to
// kill large *uploads* before ENG-1820. Files at or above this threshold
// are handed to the browser as a short-lived signed GCS GET instead of
// streaming through the API (ENG-5198). Stay well under 32 MB so headers
// cannot push a "just under" file over.
export const DIRECT_DOWNLOAD_THRESHOLD_BYTES = 16 * 1024 * 1024;

export type DirectDownloadPlan = {
  mode: "signed" | "proxy";
  url: string | null;
  filename: string;
  size_bytes: number;
};

/** True when `url` is an artifact/deliverable byte-serving GET we can probe. */
export function shouldProbeDirectDownload(url: string): boolean {
  let path: string;
  try {
    path = new URL(url, "https://example.invalid").pathname;
  } catch {
    return false;
  }
  if (/\/pages(?:\/|$)/.test(path) || /\/preview$/.test(path)) return false;
  return (
    /\/api\/artifacts\/[^/]+$/.test(path) ||
    /\/api\/tasks\/[^/]+\/artifacts\/[^/]+$/.test(path) ||
    /\/api\/deliverables\/[^/]+\/download$/.test(path) ||
    /\/api\/deliverables\/[^/]+\/versions\/[^/]+\/download$/.test(path)
  );
}

/** Append `direct=1` without duplicating it or dropping an existing query. */
export function withDirectQuery(url: string): string {
  const hashIdx = url.indexOf("#");
  const base = hashIdx === -1 ? url : url.slice(0, hashIdx);
  const hash = hashIdx === -1 ? "" : url.slice(hashIdx);
  if (/[?&]direct=/.test(base)) return url;
  const join = base.includes("?") ? "&" : "?";
  return `${base}${join}direct=1${hash}`;
}

export function isDirectDownloadPlan(value: unknown): value is DirectDownloadPlan {
  if (value == null || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    (v.mode === "signed" || v.mode === "proxy") &&
    typeof v.filename === "string" &&
    typeof v.size_bytes === "number" &&
    (v.url === null || typeof v.url === "string")
  );
}

/**
 * Replace the opaque `Failed to fetch` the browser surfaces when the GFE
 * refuses a too-large API body with a size-aware message. Structured
 * `ApiError`s (auth, 404, 400) pass through unchanged.
 */
export function downloadFailureMessage(err: unknown, sizeBytes?: number | null): string {
  const raw =
    err instanceof Error && err.message
      ? err.message
      : typeof err === "string"
        ? err
        : "Download failed";
  // ApiError.detail is copied onto Error.message by the ApiError constructor.
  const opaque = /failed to fetch|networkerror|load failed/i.test(raw);
  if (!opaque) return raw;
  if (sizeBytes != null && sizeBytes >= DIRECT_DOWNLOAD_THRESHOLD_BYTES) {
    const mb = (sizeBytes / (1024 * 1024)).toFixed(1);
    return (
      `This file is ${mb} MB — too large to download through the app. ` +
      "Refresh and try again; large files should save directly."
    );
  }
  return "Couldn't reach the file. Check your connection and try again.";
}
